import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatButton } from '@framework/ui/atoms';
import { Link } from 'react-router';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { EditPasswordForm } from '@user/EditPasswordForm';
import { Loading } from '@framework/ui/atoms/Loading';
import { trpcClient } from '@framework/trpcClient';

type TokenStatus = Awaited<ReturnType<typeof trpcClient.user.auth.fetchPasswordResetToken.query>>['status'] | 'loading';

type Props = {
  resetTokenId: string;
  onResetSuccess(): void;
};

export const ResetForm: React.FC<Props> = ({ resetTokenId, onResetSuccess }: Props) => {
  const { t } = useTranslation();
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>('loading');

  useEffect(() => {
    trpcClient.user.auth.fetchPasswordResetToken
      .query({ resetTokenId })
      .then(({ status }) => {
        setTokenStatus(status);
      })
      .catch((error) => {
        setTokenStatus('invalid');
        console.error('Failed to fetch password reset token:', error);
      });
  }, [resetTokenId]);

  const [password, setPassword] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleChangePassword = useCallback((value: string) => {
    setPassword(value);
  }, []);

  const handleClick = useCallback(async () => {
    setProcessing(true);

    try {
      await trpcClient.user.auth.resetPassword.mutate({ resetTokenId, password });
      onResetSuccess();
    } catch (error) {
      setError(t('auth.passwordReset.resetError'));
      console.error(error);
    } finally {
      setProcessing(false);
    }
  }, [onResetSuccess, password, resetTokenId, t]);

  const isValid = !!password;

  if (tokenStatus === null) {
    return <Loading />;
  }

  if (tokenStatus === 'notFound') {
    return <div className="py-4">{t('auth.passwordReset.tokenNotFound')}</div>;
  }

  if (tokenStatus === 'invalid') {
    return (
      <>
        <div className="py-4">{t('auth.passwordReset.tokenInvalid')}</div>
        <div className="pt-4">
          <Link to={UserPagePathBuilder.passwordResetPage()} className="text-sm text-blue-700 hover:underline">
            {t('auth.passwordReset.getResetLink')}
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="py-4">{t('auth.passwordReset.newPasswordDescription')}</div>

      <EditPasswordForm onChange={handleChangePassword} disabled={processing} />

      {error && <div className="text-red-500">{error}</div>}

      <div className="pb-8 pt-16 text-center">
        <FlatButton
          className="px-16"
          color="brand"
          onClick={handleClick}
          disabled={!isValid || processing}
          loading={processing}
        >
          {t('auth.passwordReset.pageTitle')}
        </FlatButton>
      </div>
    </>
  );
};
